import React, { Fragment, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Show } from '../../components/show';

import CorrectionBubbles from '../correction-bubbles';
import Button from '../../components/button';
import CorrectionResults from '../../components/correction-results';
import CorrectionResultsBySections from '../../components/correction-results-by-sections';
import FluidContainer from '../../components/layouts/components/fluid-container';
import Hidden from '../../components/layouts/components/hidden';
import ScoreSection from './score-section';
import { useMathJaxStatus, useOnlineStatus } from '../../lib/hooks';
import { BASE_NAME_APP } from '../../config';
import { SOURCES } from '../../constants/sources';
import { APPLICATION_MODE } from '../../constants/layout';
import { RESULTS } from '../../constants/routes';
import { BubbleTypes } from '../../components/bubble';

const CorrectionTitle = styled.div`
    font-size: 24px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 32px;
    margin-bottom: 16px;
    margin-top: 16px;
`;

const GridColumn = styled.div`
    @media (max-width: 767px) {
        order: ${(props) => (props.reverse ? -1 : 1)};
    }
`;

export const Title = styled.span`
    font-size: 28px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 36px;
    margin-bottom: 8px;
    margin-top: 0;
`;

export const Message = styled.span`
    font-size: 16px;
    letter-spacing: 0;
    line-height: 24px;
    margin-top: 0;
    padding-bottom: ${(props) => props.theme.space.base}px;
`;

export const BoldMessage = styled(Message)`
    font-weight: bold;
`;

export const AlertMessage = styled(Message)`
    color: var(--red800);
`;

const Container = styled(FluidContainer)`
    @media print {
        padding-top: 0;
    }
`;

export const ButtonContainer = styled.div`
    margin-bottom: 24px;

    @media (min-width: 768px) {
        width: fit-content;
    }

    @media (max-width: 767px) {
        & > a > button {
            width: 100%;
        }
    }
`;

const Link = styled.span`
    color: var(--blue500);
    text-decoration: underline;
    cursor: pointer;
`;

export const getPaddingTop = (applicationMode, exercisesNumber, currentApplicationPage) => {
    // The first 6 arrays of padding ([mobile, tablet, desktop] each) are for execution page, the others 6 for results page.
    // In each group of 6 arrays, the first 3 are for homework with only 1 exercise, the others 3 for homework with more than 1 exercise.
    // In each of the last described group of 3 arrays, the first array is for default application mode,the 2nd one for
    // light application mode and the 3rd one for embedded application mode.
    const paddings = [
        [172, 168, 136], // Execution page, default mode, 1 exercise
        [20, 20, 20], // Execution page, light mode, 1 exercise
        [28, 28, 40], // Execution page, embedded mode, 1 exercise
        [220, 216, 136], // Execution page, default mode, >1 exercises
        [68, 68, 68], // Execution page, light mode, >1 exercises
        [76, 76, 88], // Execution page, embedded mode, >1 exercises
        [172, 168, 136], // Results page, default mode, 1 exercise
        [16, 16, 88], // Results page, light mode, 1 exercise
        [28, 28, 40], // Results page, embedded mode, 1 exercise
        [220, 216, 136], // Results page, default mode, >1 exercises
        [64, 64, 136], // Results page, light mode, >1 exercises
        [76, 76, 88], // Results page, embedded mode, >1 exercises
    ];
    let offset = 0;

    if (currentApplicationPage === RESULTS) {
        offset += 6;
    }

    if (exercisesNumber > 1) {
        offset += 3;
    }

    if (applicationMode === APPLICATION_MODE.LIGHT) {
        offset += 1;
    } else if (applicationMode === APPLICATION_MODE.EMBEDDED) {
        offset += 2;
    }

    return paddings[offset];
};
const getTaxonomyNode = (node) => {
    if (node.child) {
        return getTaxonomyNode(node.child);
    }
    return node;
};

const sendPostMessage = (event, toReviewNodes) => {
    window.parent.postMessage(
        {
            event: event,
            toReviewNodes,
        },
        '*',
    );
};

const groupExercisesByTopic = (exercises, correctionsStatus) => {
    let groupedExercises = exercises.reduce((map, e, executionIndex) => {
        const contentPath = e.contentPaths[0];
        const paths = contentPath.paths.filter((p) => p.verb === 1);

        paths[0].paths.forEach((path) => {
            const specificNode = getTaxonomyNode(path);

            let argument = {
                details: specificNode,
                exercises: [],
            };
            if (!map.has(specificNode.name)) {
                map.set(specificNode.name, argument);
            } else {
                argument = map.get(specificNode.name);
            }
            argument.exercises.push({
                ...e,
                executionIndex,
            });
        });

        return map;
    }, new Map());

    //ordinamento alfabetico per argomenti
    //groupedExercises = new Map([...groupedExercises.entries()].sort());

    let exerciseList = [];
    const sections = [];
    groupedExercises.forEach((argument, key) => {
        const { details, exercises } = argument;
        exerciseList = [...exerciseList, ...exercises];

        let toReview = false;
        for (const exercise of exercises) {
            const exerciseStatus = correctionsStatus.find((s) => exercise.id === s.exerciseId);
            if (exerciseStatus && exerciseStatus.state !== BubbleTypes.correct) {
                toReview = true;
                break;
            }
        }

        sections.push({
            name: key,
            exercisesCount: exercises.length,
            completed: true,
            toReview,
            nodeDetails: details,
        });
    });
    return { sections, exerciseList };
};

export const ReviewLinks = ({ sections }) => {
    const links = sections
        .filter((s) => s.toReview)
        .map((section, i, filteredSections) => {
            let separator = ', ';
            if (i === filteredSections.length - 2) {
                separator = ' e ';
            } else if (i === filteredSections.length - 1) {
                separator = '.';
            }
            return (
                <Fragment key={i}>
                    <Link onClick={() => sendPostMessage('review', [section.nodeDetails.id])}>{section.nodeDetails.name}</Link>
                    {separator}
                </Fragment>
            );
        });
    if (links.length === sections.length) {
        return <Message>{'Ti consigliamo di ripassare tutti gli argomenti trattati nelle prove.'}</Message>;
    }
    const plural = links.length > 1;
    return (
        <Message>
            {`Ripassa i${plural ? '' : 'l'} percors${plural ? 'i' : 'o'} `}
            {links}
        </Message>
    );
};

ReviewLinks.propTypes = {
    groupCorrectionByTopic: PropTypes.bool,
    sections: PropTypes.array,
};

const ResultsPage = ({
    answers = [],
    exercises,
    videos,
    onPlayVideo,
    correctionPoints,
    correctionsStatus,
    maxAttemptsNumber,
    attemptsNumber,
    assignmentId,
    assignmentEndDate,
    groupCorrectionByTopic,
    getStatusArray,
    geogebraExercises,
    encodedPayload,
    loaded,
    correctable,
    sendGradeError,
    showSendGradeErrorModal,
    sendGradeErrorModalShowed,
    status,
    saveStatus,
    getStatusById,
    calculateVoteTraining,
    source,
    homeworkId,
    bookId,
    isApplicationEmbedded,
    isPreview,
    isGoogleAddon,
    mode,
}) => {
    const [correctionType, setCorrectionType] = useState('ALL');
    const [expand, setExpand] = useState(false);
    const isMathJaxLoading = useMathJaxStatus();
    const onlineStatus = useOnlineStatus();
    const [sections, setSection] = useState([]);
    const [exerciseList, setExerciseList] = useState([]);
    const remainingAttemptsNumber = maxAttemptsNumber - attemptsNumber;
    const attemptsLabel = remainingAttemptsNumber === 1 ? 'tentativo' : 'tentativi';
    const usethemLabel = remainingAttemptsNumber === 1 ? 'usarlo' : 'usarli';
    const baseAppUrl = BASE_NAME_APP ?? '';
    const showLazeResult = source === SOURCES.LAZE && !assignmentEndDate;

    let assignmentClosed = false;
    if (assignmentEndDate) {
        const assignmentEndDateTime = new Date(assignmentEndDate);
        assignmentClosed = assignmentEndDateTime.getTime() < Date.now();
    }

    useEffect(() => {
        if (assignmentId && source !== SOURCES.LAZE) {
            getStatusArray(assignmentId);
        }
    }, [assignmentId, getStatusArray, source]);

    useEffect(() => {
        if (sendGradeError && !sendGradeErrorModalShowed) {
            showSendGradeErrorModal();
        }
    }, [showSendGradeErrorModal, sendGradeError, sendGradeErrorModalShowed]);

    useEffect(() => {
        if (saveStatus) {
            getStatusById(status.id);
        }
    }, [status.id, getStatusById, saveStatus]);

    useEffect(() => {
        if (!saveStatus) {
            calculateVoteTraining(source, homeworkId, bookId, status.homeworkVersion, answers, status.scoreModifier);
        }
    }, [calculateVoteTraining, saveStatus, source, homeworkId, bookId, status.homeworkVersion, answers, status.scoreModifier]);

    useEffect(() => {
        if (correctionsStatus && exercises && correctionsStatus.length === exercises.length) {
            const { sections, exerciseList } = groupCorrectionByTopic
                ? loaded && groupExercisesByTopic(exercises, correctionsStatus)
                : { sections: status.sections || null, exerciseList: exercises };

            if (groupCorrectionByTopic && sections.length > 0) {
                const toReviewNodes = sections.filter((s) => s.toReview).map((s) => s.nodeDetails.id);
                sendPostMessage('completed', toReviewNodes);
            }

            setSection(sections);
            setExerciseList(exerciseList);
        }
    }, [groupCorrectionByTopic, exercises, correctionsStatus, loaded, status.sections]);

    const onReviewClick = () => {
        const filteredSections = status.exercisesCorrection.CORRECT === exercises.length ? sections : sections.filter((s) => s.toReview);
        const toReviewNodes = filteredSections.map((s) => s.nodeDetails.id);
        sendPostMessage('review', toReviewNodes);
    };

    const generateTutorResultsString = (numberOfCorrectExercises, numberOfExercises) => {
        if (numberOfCorrectExercises === 0) {
            return <BoldMessage>{"Purtroppo nessuna delle tue risposte era corretta. Non perderti d'animo!"}</BoldMessage>;
        }

        if (numberOfCorrectExercises === numberOfExercises) {
            return (
                <>
                    <BoldMessage>{'Hai risposto bene a tutti i quesiti. Complimenti!'}</BoldMessage>
                    <Message>{'Puoi continuare il ripasso, per migliorare ancora.'}</Message>
                </>
            );
        }

        return (
            <BoldMessage>{`Hai risposto bene a ${numberOfCorrectExercises} quesit${
                numberOfCorrectExercises === 1 ? 'o' : 'i'
            } su ${numberOfExercises}. Ci sei quasi!`}</BoldMessage>
        );
    };

    return (
        <Container flexDirection="column" paddingTop={getPaddingTop(mode, exercises.length, RESULTS)}>
            <div className="z-grid">
                <GridColumn className="mobile-col-4 tablet-col-4 col-7">
                    <Hidden print={true}>
                        <FluidContainer flexDirection="column">
                            <Title>{'Risultati'}</Title>
                            <Show when={(correctable || !assignmentId || assignmentClosed || showLazeResult) && !isGoogleAddon}>
                                <ScoreSection />
                                <CorrectionBubbles
                                    onClick={(value) => (correctionType === value ? setCorrectionType('ALL') : setCorrectionType(value))}
                                    selectedBubble={correctionType}
                                />
                            </Show>
                            <Show when={(!correctable && assignmentId && !assignmentClosed && !showLazeResult) || isGoogleAddon}>
                                <AlertMessage>
                                    {'Attenzione: potrai vedere voto e correzione delle verifiche assegnate solo dopo la data di consegna.'}
                                </AlertMessage>
                            </Show>
                        </FluidContainer>
                    </Hidden>
                </GridColumn>
                <GridColumn className="mobile-col-4 tablet-col-4 col-5" reverse={true}>
                    <Hidden print={true}>
                        <FluidContainer flexDirection="column">
                            <Show when={loaded && !isApplicationEmbedded && source !== SOURCES.LAZE && !groupCorrectionByTopic}>
                                <Title>{'Tentativi'}</Title>
                                {assignmentId && assignmentClosed ? (
                                    <BoldMessage>{'Il tempo a disposizione per svolgere questa prova è scaduto. Peccato!'}</BoldMessage>
                                ) : maxAttemptsNumber === 0 ? (
                                    <>
                                        <Message>{'Vuoi fare di meglio? Puoi rifare la prova.'}</Message>
                                        <ButtonContainer>
                                            <a href={`${baseAppUrl}/?payload=${encodedPayload}`}>
                                                <Button size="small" disabled={!onlineStatus}>
                                                    {'Ritenta'}
                                                </Button>
                                            </a>
                                        </ButtonContainer>
                                    </>
                                ) : remainingAttemptsNumber > 0 ? (
                                    <>
                                        <Hidden above="tablet">
                                            <Message>
                                                {'Vuoi fare di meglio?'}
                                                <b>{` Hai ancora ${remainingAttemptsNumber} ${attemptsLabel}.`}</b>
                                            </Message>
                                        </Hidden>
                                        <Hidden below="tablet">
                                            <Message>
                                                {'Vuoi fare di meglio?'} <br />
                                                <b>{`Hai ancora a disposizione ${remainingAttemptsNumber} ${attemptsLabel}:`}</b>
                                                <br />
                                                {`puoi anche ${usethemLabel} in un altro momento.`}
                                            </Message>
                                        </Hidden>
                                        <ButtonContainer>
                                            <a href={`${baseAppUrl}/?payload=${encodedPayload}`}>
                                                <Button size="small" disabled={!onlineStatus}>
                                                    {'Ritenta'}
                                                </Button>
                                            </a>
                                        </ButtonContainer>
                                    </>
                                ) : (
                                    <BoldMessage>{"Questo era l'ultimo tentativo a tua disposizione."}</BoldMessage>
                                )}
                            </Show>
                            <Show className="tutor-results" when={loaded && groupCorrectionByTopic}>
                                <Title>{"Com'è andata?"}</Title>

                                {generateTutorResultsString(status.exercisesCorrection.CORRECT, exercises.length)}
                                {status.exercisesCorrection.CORRECT !== 0 && status.exercisesCorrection.CORRECT !== exercises.length && (
                                    <ReviewLinks sections={sections} />
                                )}
                                <ButtonContainer>
                                    <Button size="small" disabled={!onlineStatus} onClick={onReviewClick}>
                                        {'Avvia il ripasso'}
                                    </Button>
                                </ButtonContainer>
                            </Show>
                            <Show
                                when={
                                    loaded &&
                                    !isApplicationEmbedded &&
                                    source === SOURCES.LAZE &&
                                    maxAttemptsNumber === 0 &&
                                    !groupCorrectionByTopic &&
                                    !isPreview
                                }
                            >
                                <Title>{'Tentativi'}</Title>
                                <>
                                    <Message>{'Vuoi fare di meglio? Puoi rifare la prova.'}</Message>
                                    <ButtonContainer>
                                        <a href={`${baseAppUrl}/?payload=${encodedPayload}`}>
                                            <Button size="small" disabled={!onlineStatus}>
                                                {'Ritenta'}
                                            </Button>
                                        </a>
                                    </ButtonContainer>
                                </>
                            </Show>
                        </FluidContainer>
                    </Hidden>
                </GridColumn>
            </div>

            <Show when={(correctable || !assignmentId || assignmentClosed || showLazeResult) && !isGoogleAddon}>
                <FluidContainer alignItems="center" justifyContent="space-between">
                    <CorrectionTitle>{'Correzione'}</CorrectionTitle>
                    <Hidden print={true}>
                        <FluidContainer>
                            <Hidden below="tablet">
                                <Button type="tertiary" onClick={() => window.print()} size="small" disabled={isMathJaxLoading}>
                                    {'Stampa'}
                                </Button>
                            </Hidden>

                            <Button type="tertiary" onClick={() => setExpand(!expand)} size="small">
                                {expand ? 'NASCONDI TUTTO' : 'VEDI TUTTO'}
                            </Button>
                        </FluidContainer>
                    </Hidden>
                </FluidContainer>

                {sections && sections.length > 0 ? (
                    <CorrectionResultsBySections
                        answers={answers}
                        exercises={exerciseList}
                        filter={correctionType}
                        expand={expand}
                        correctionsStatus={correctionsStatus}
                        videos={videos}
                        onPlayVideo={onPlayVideo}
                        geogebraExercises={geogebraExercises}
                        correctionPoints={correctionPoints}
                        source={source}
                        sections={sections}
                        scoreModifier={status.scoreModifier}
                        showScore={!groupCorrectionByTopic}
                    />
                ) : (
                    <CorrectionResults
                        answers={answers}
                        exercises={exercises}
                        filter={correctionType}
                        expand={expand}
                        correctionsStatus={correctionsStatus}
                        videos={videos}
                        onPlayVideo={onPlayVideo}
                        geogebraExercises={geogebraExercises}
                        correctionPoints={correctionPoints}
                        source={source}
                    />
                )}
            </Show>
        </Container>
    );
};

ResultsPage.propTypes = {
    assignmentId: PropTypes.string,
    assignmentEndDate: PropTypes.string,
    answers: PropTypes.array,
    attemptsNumber: PropTypes.number,
    groupCorrectionByTopic: PropTypes.bool,
    exercises: PropTypes.any,
    maxAttemptsNumber: PropTypes.number,
    correctionsStatus: PropTypes.array,
    videos: PropTypes.array,
    onPlayVideo: PropTypes.func,
    getStatusArray: PropTypes.func,
    geogebraExercises: PropTypes.array,
    encodedPayload: PropTypes.string,
    loaded: PropTypes.bool,
    correctable: PropTypes.bool,
    sendGradeError: PropTypes.bool,
    showSendGradeErrorModal: PropTypes.func,
    sendGradeErrorModalShowed: PropTypes.bool,
    correctionPoints: PropTypes.array,
    status: PropTypes.object,
    saveStatus: PropTypes.bool,
    getStatusById: PropTypes.func,
    source: PropTypes.string,
    calculateVoteTraining: PropTypes.func,
    homeworkId: PropTypes.string,
    bookId: PropTypes.number,
    isApplicationEmbedded: PropTypes.bool,
    isPreview: PropTypes.bool,
    isGoogleAddon: PropTypes.bool,
    mode: PropTypes.oneOf([APPLICATION_MODE.LIGHT, APPLICATION_MODE.DEFAULT, APPLICATION_MODE.EMBEDDED, '']),
};

export default ResultsPage;
